"use client";

import { useEffect, useRef } from "react";

const WORKFLOW_PROGRESS_REFRESH_DELAYS_MS = [0, 1_500, 3_500, 7_000, 12_000, 20_000];

export function refreshTprWorkflowProgress(refresh: () => void): void {
  for (const delay of WORKFLOW_PROGRESS_REFRESH_DELAYS_MS) {
    window.setTimeout(refresh, delay);
  }
}

/**
 * How often the open sync-steps view re-reads the EHR. Each tick re-runs the server component,
 * which re-issues the live Athena reads in `fetchTprPageData` — that is the point: ops fixes
 * insurance (or a provider) directly in Athena and expects the step cards to catch up without a
 * reload. Kept at a minute because those are several real Athena calls per tick, per open task.
 */
const TPR_LIVE_REFRESH_INTERVAL_MS = 60_000;

/**
 * Re-reads the EHR on an interval while the sync-steps view is mounted, and once more whenever the
 * tab regains focus — ops typically alt-tabs to Athena, changes something, and comes straight back,
 * which would otherwise wait out the rest of the interval.
 *
 * An Effect is correct here: this synchronizes React with two external systems (a timer and the
 * document's visibility), which is exactly what Effects are for. It deliberately does not poll
 * while the tab is hidden.
 */
export function useTprLiveWorkflowRefresh(refresh: () => void): void {
  // `useInvalidateServerComponent` hands back a new function identity every render, so the timer
  // is driven off a ref instead. That keeps one interval for the life of the view rather than
  // tearing down and re-arming it on each render, without memoizing a callback.
  const refreshRef = useRef(refresh);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") refreshRef.current();
    };

    const interval = window.setInterval(refreshIfVisible, TPR_LIVE_REFRESH_INTERVAL_MS);

    document.addEventListener("visibilitychange", refreshIfVisible);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, []);
}
